// Copyright (c) 2020, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React from 'react'
import PropTypes from 'prop-types'
import { graphql, compose, Mutation } from 'react-apollo'
import gql from 'graphql-tag'
import { default as fragments } from '../../../fragments'
import { withRouter } from 'react-router-dom'
import moment from 'moment'

import {
  SvgIcon,
  Table,
  Spinner,
  PageNavigationButtons,
  Button
} from '../../'

import { MessageTableItem } from './MessageTableItem'

import {
  PageNavigationControl,
  getPageCursor,
  decodeBase64
} from '../../../utils'


const MUTATION_MESSAGE = gql`
  mutation($input: TelegramMessageMutationInput!) {
    telegrammessageMutate(input: $input) {
      message: telegrammessage {
        id
        status
      }
    }
  }
`


class EmployerBulkMessageTelegramMessages extends React.Component {
  static propTypes = {
    bulkMessageId: PropTypes.string.isRequired,
    status: PropTypes.string,
    direction: PropTypes.string,
    messageType: PropTypes.string,
    fromAddr: PropTypes.string,
    toAddr: PropTypes.string,
    scheduledAtFrom: PropTypes.string,
    scheduledAtTo: PropTypes.string,
    timestampFrom: PropTypes.string,
    timestampTo: PropTypes.string,
    unknownParticipant: PropTypes.bool,
    orderBy: PropTypes.string,
    changeSort: PropTypes.func,
    resultsPerPage: PropTypes.number,
    numberOfFailedMessages: PropTypes.number,
    refresh: PropTypes.func.isRequired,
    resendFailedMessages: PropTypes.func.isRequired,
    className: PropTypes.string
  }

  static defaultProps = {
    resultsPerPage: 15,
    className: ''
  }

  state = {
    selectedMessage: null,
    replyToMessage: false,
    filterChanged: false,
    pageNav: new PageNavigationControl()
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if ((this.props.status !== nextProps.status ||
      this.props.direction !== nextProps.direction ||
      this.props.messageType !== nextProps.messageType ||
      this.props.scheduledAtFromDate !== nextProps.scheduledAtFromDate ||
      this.props.scheduledAtToDate !== nextProps.scheduledAtToDate ||
      this.props.orderBy !== nextProps.orderBy) &&
      !sessionStorage.getItem('messageListPageState')
    ) {
      let previousFilters = JSON.parse(sessionStorage.getItem('messageListFilters'))
      if (previousFilters && (
        previousFilters.status === nextProps.status ||
        previousFilters.direction === nextProps.direction ||
        previousFilters.messageType === nextProps.messageType ||
        previousFilters.scheduledAtFromDate === nextProps.scheduledAtFromDate ||
        previousFilters.scheduledAtToDate === nextProps.scheduledAtToDate ||
        this.props.orderBy !== nextProps.orderBy
      )) {
        sessionStorage.removeItem('messageListFilters')
      } else {
        this.state.pageNav.reset()
        this.setState({
          filterChanged: true
        }, () => this.props.refetch())
        sessionStorage.removeItem('messageListFilters')
      }
    } else {
      if (!nextProps.loading) {
        if (this.state.filterChanged) {
          this.setState({
            filterChanged: false
          })
          this.state.pageNav.setPageStates(null, nextProps.messages)
        }
        else if (nextProps.messages) {
          let messageListPageState = JSON.parse(sessionStorage.getItem('messageListPageState'))
          if (messageListPageState) {
            this.state.pageNav.setPageStates(null, null, messageListPageState)
            sessionStorage.removeItem('messageListPageState')
          } else {
            this.state.pageNav.setPageStates(this.props.messages, nextProps.messages)
          }
        }
      }
    }
  }

  resendMessage = (telegrammessageMutate, messageId) => (
    telegrammessageMutate({
      variables: {
        input: {
          id: messageId,
          status: 'unsent'
        }
      }
    })
  )

  getTableRows = (messages, dueToBeSent) => (
    messages.edges.map(({ node: message }) => (
      <MessageTableItem
        employerId={this.props.match.params.employerId}
        identityId={message.toIdentity.id}
        identityFullName={message.toIdentity.fullName}
        identityIdentityValue={message.toIdentity.identityValue}
        status={message.status}
        action={['UNDELIVERABLE'].indexOf(message.status) > -1 &&
          dueToBeSent &&
          <Mutation mutation={MUTATION_MESSAGE}>
            {telegrammessageMutate => (
              <Button
                type='reset'
                onClick={() => this.resendMessage(telegrammessageMutate, message.id)}>
                Resend?
              </Button>
            )}
          </Mutation>
        }
        expandMessage={this.state.selectedMessage === message.id}
        // onClick={messageId => (
        //   this.props.history.push(`/employer/${this.props.match.params.employerId}/communications/messages/${messageId}`)
        // )}
        key={`message_${message.id}`} />
    ))
  )

  render() {
    if (this.props.loading) {
      return <Spinner className='slds-is-relative' />
    }

    if (this.props.error) {
      return <h1>Oh dear! We have an issue</h1>
    }

    let pageNav = this.state.pageNav
    let columns = ['To', 'ID', 'Status', 'Created at', '']
    if (this.props.actionColumn) {
      columns.push(this.props.actionColumn.name)
    }

    let messages = this.props.messages
    let startCursor = messages.pageInfo.startCursor
    let totalCount = messages.totalCount

    if (messages.edges.length === 0) {
      return (
        <div className={this.props.className}>
          <div className='slds-m-left_small slds-p-around_medium u-font-size-larger'>
              <SvgIcon
                variant='utility'
                symbol='info'
                size='small'
                colour='light'
                className='slds-m-right_small'
              />
              No associated Telegram messages.
              <Button
                type='reset'
                onClick={this.props.refresh}
                className='slds-p-left_small'>
                Refresh?
              </Button>
            </div>
        </div>
      )
    }
    else {
      const dueToBeSent = messages.edges[0].node.scheduledAt
        ? moment(messages.edges[0].node.scheduledAt).isBefore(moment())
        : true
      // only need to check the first individual message generated from bulk as all with inherit
      // same scheduled at date from bulk message creation

      return (
        <div className={this.props.className}>
          <div>
            <div className='slds-grid slds-p-around_x-small' style={{ backgroundColor: '#6c818f' }}>
              <Button
                title='Attempt to resend messages that failed to sent before'
                onClick={this.props.resendFailedMessages}
                disabled={dueToBeSent
                  ? this.props.numberOfFailedMessages === 0
                  : true
                }
                className='slds-col_bump-left u-font-weight-normal slds-float_right'>
                Resend failed messages
              </Button>
              <Button
                onClick={this.props.refresh}
                disabled={this.props.hasUnsentMessages}>
                Refresh
              </Button>
            </div>
            <Table
              columns={columns}
              rows={this.getTableRows(messages, dueToBeSent)}
              objectRows={true}
              orderBy={this.props.orderBy}
              changeSort={this.props.changeSort}
              querySortableColumns={{
                'Date/Time': {
                  orderByName: 'timestamp'
                }
              }}
            />
            <PageNavigationButtons
              onNextClick={() => pageNav.nextPage(this.props.fetchNext)}
              onPreviousClick={() => pageNav.previousPage(this.props.fetchPrevious)}
              previousDisabled={pageNav.firstPage}
              nextDisabled={pageNav.lastPage}
              startCursor={startCursor}
              totalCount={totalCount}
              resultsPerPage={this.props.resultsPerPage}
              resultsText='messages'
              onPageNumberClick={pageNumber => this.props.refetch({ cursor: getPageCursor(pageNumber, this.props.resultsPerPage) })}
              className="slds-p-around_medium slds-float_right"
            />
          </div>
        </div>
      )
    }
  }
}

const getMessages = gql`
  query($bulkMessageId: UUID!, $orderBy: String, $resultsPerPage: Int!, $cursor: String) {
    messages: telegrammessages(bulkmessage_Id: $bulkMessageId, orderBy: $orderBy, first: $resultsPerPage, after: $cursor) {
      totalCount
      edges {
        node {
          ...TelegramMessage
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
  ${fragments.telegrammessage}
`

const getPreviousMessages = gql`
  query($bulkMessageId: UUID!, $orderBy: String, $resultsPerPage: Int!, $cursor: String) {
    messages: telegrammessages(bulkmessage_Id: $bulkMessageId, orderBy: $orderBy, last: $resultsPerPage, before: $cursor) {
      totalCount
      edges {
        node {
          ...TelegramMessage
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
  ${fragments.telegrammessage}
`

const withData = graphql(getMessages, {
  options: ({ bulkMessageId, resultsPerPage = 15, orderBy }) => ({
    variables: {
      bulkMessageId: decodeBase64(bulkMessageId),
      resultsPerPage,
      orderBy
    },
    fetchPolicy: 'network-only'
  }),
  props: ({ ownProps: { bulkMessageId, resultsPerPage = 15, orderBy }, data: { loading, error, refetch, messages, fetchMore } }) => ({
    loading,
    error,
    refetch,
    messages,
    fetchNext: () => {
      return fetchMore({
        query: getMessages,
        variables: {
          bulkMessageId: decodeBase64(bulkMessageId),
          orderBy,
          resultsPerPage,
          cursor: messages.pageInfo.endCursor
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const newEdges = fetchMoreResult.messages.edges
          const pageInfo = fetchMoreResult.messages.pageInfo
          return {
            messages: {
              __typename: previousResult.messages.__typename,
              edges: [...newEdges],
              pageInfo
            }
          }
        }
      })
    },
    fetchPrevious: () => {
      return fetchMore({
        query: getPreviousMessages,
        variables: {
          bulkMessageId: decodeBase64(bulkMessageId),
          orderBy,
          resultsPerPage,
          cursor: messages.pageInfo.startCursor
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const newEdges = fetchMoreResult.messages.edges
          const pageInfo = fetchMoreResult.messages.pageInfo
          return {
            messages: {
              __typename: previousResult.messages.__typename,
              edges: [...newEdges],
              pageInfo
            }
          }
        }
      })
    }
  })
})

export default compose(
  withData,
  withRouter
)(EmployerBulkMessageTelegramMessages)



// WEBPACK FOOTER //
// ./src/components/messageplus/employer-bulk-message-telegram-messages/index.js